
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Mail,
    Phone,
    Building2,
    FileText,
    Home,
    Calendar,
    CheckCircle,
    Ban,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { toast } from '@/hooks/use-toast';
import { getUserById, updateUserStatus } from '@/services/user';

interface BrokerProfile {
    _id: string;
    name: string;
    email: string;
    phone: string;
    company?: string;
    licenseNumber?: string;
    accountStatus: boolean;
    userType: string;
    createdAt: string;
}

const BrokerProfile = () => {
    const { id } = useParams<{ id: string }>();
    const [broker, setBroker] = useState<BrokerProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (id) {
            fetchBrokerProfile();
        }
    }, [id]);

    const fetchBrokerProfile = async () => {
        setIsLoading(true);
        try {
            const response = await getUserById(id!);
            setBroker(response);
        } catch (error: any) {
            console.error('Error fetching broker profile:', error);
            toast({
                title: "Error",
                description: error.message || "Failed to load broker profile",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusToggle = async () => {
        if (!broker) return;

        setIsUpdating(true);
        try {
            await updateUserStatus(broker._id, !broker.accountStatus);
            toast({
                title: "Status Updated",
                description: `Broker account ${!broker.accountStatus ? 'activated' : 'deactivated'} successfully`,
            });
            // Refresh broker data
            fetchBrokerProfile();
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to update broker status",
                variant: "destructive"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header />
                <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                    <div className="container mx-auto px-4 md:px-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                            <div className="space-y-2">
                                <Skeleton className="h-10 w-64" />
                                <Skeleton className="h-4 w-32" />
                            </div>
                            <Skeleton className="h-10 w-40" />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 space-y-6">
                                <Card>
                                    <CardHeader>
                                        <Skeleton className="h-6 w-48 mb-2" />
                                        <Skeleton className="h-4 w-64" />
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <Skeleton className="h-4 w-20" />
                                                <Skeleton className="h-6 w-32" />
                                            </div>
                                            <div className="space-y-2">
                                                <Skeleton className="h-4 w-24" />
                                                <Skeleton className="h-8 w-24 rounded-full" />
                                            </div>
                                        </div>
                                        <Separator />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="flex items-center gap-3">
                                                <Skeleton className="h-5 w-5 rounded-full" />
                                                <div className="space-y-1">
                                                    <Skeleton className="h-3 w-16" />
                                                    <Skeleton className="h-4 w-40" />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Skeleton className="h-5 w-5 rounded-full" />
                                                <div className="space-y-1">
                                                    <Skeleton className="h-3 w-16" />
                                                    <Skeleton className="h-4 w-32" />
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <Skeleton className="h-6 w-32" />
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <Skeleton className="h-10 w-full" />
                                        <Skeleton className="h-10 w-full" />
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!broker) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header />
                <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                    <div className="container mx-auto px-4 md:px-6">
                        <Card>
                            <CardContent className="py-12 text-center">
                                <p className="text-muted-foreground">Broker not found</p>
                                <Button asChild className="mt-4">
                                    <Link to="/admin/brokers">
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back to Brokers
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Header />

            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold capitalize">{broker.name}</h1>
                            <p className="text-muted-foreground">Broker Profile</p>
                        </div>
                        <Button variant="outline" asChild>
                            <Link to="/admin/dashboard">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Dashboard
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Info */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Basic Information */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Basic Information</CardTitle>
                                    <CardDescription>Broker's personal and contact details</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                                            <p className="text-lg capitalize">{broker.name}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Account Status</label>
                                            <div className="mt-1">
                                                <Badge className={broker.accountStatus ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 text-red-800 hover:bg-red-100'}>
                                                    {broker.accountStatus ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>

                                    <Separator />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center gap-3">
                                            <Mail className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Email</label>
                                                <p className="text-base">{broker.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Phone className="h-5 w-5 text-muted-foreground" />
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Phone</label>
                                                <p className="text-base">{broker.phone}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <Separator />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {broker.company && (
                                            <div className="flex items-center gap-3">
                                                <Building2 className="h-5 w-5 text-muted-foreground" />
                                                <div>
                                                    <label className="text-sm font-medium text-muted-foreground">Company</label>
                                                    <p className="text-base capitalize">{broker.company}</p>
                                                </div>
                                            </div>
                                        )}
                                        {broker.licenseNumber && (
                                            <div className="flex items-center gap-3">
                                                <FileText className="h-5 w-5 text-muted-foreground" />
                                                <div>
                                                    <label className="text-sm font-medium text-muted-foreground">License Number</label>
                                                    <p className="text-base">{broker.licenseNumber}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <Separator />

                                    <div className="flex items-center gap-3">
                                        <Calendar className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Joined Date</label>
                                            <p className="text-base">{formatDate(broker.createdAt)}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Quick Actions */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Quick Actions</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <Button variant="outline" className="w-full justify-start" asChild>
                                        <Link to={`/admin/property-management?broker=${broker._id}`}>
                                            <Home className="h-4 w-4 mr-2" />
                                            View Listings
                                        </Link>
                                    </Button>
                                    <Button
                                        variant={broker.accountStatus ? "destructive" : "default"}
                                        className="w-full justify-start"
                                        onClick={handleStatusToggle}
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? (
                                            <>Processing...</>
                                        ) : broker.accountStatus ? (
                                            <>
                                                <Ban className="h-4 w-4 mr-2" />
                                                Deactivate Account
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                Activate Account
                                            </>
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default BrokerProfile;
